/**
 * A2A Message Service
 * Wraps message/group operations using A2A client instead of direct Supabase calls
 */

import { A2AClient } from '../client.js';
import { getAuthToken } from '../authHelper.js';

// Initialize A2A client with default backend URL and authentication
const a2aClient = new A2AClient(
  process.env.REACT_APP_A2A_BACKEND_URL || process.env.REACT_APP_API_URL || 'https://a2amyclassai-server.appsokay.net',
  'myclass-client-messages',
  {
    secretKey: process.env.REACT_APP_A2A_SECRET_KEY,
    enableLogging: process.env.NODE_ENV === 'development',
    getAuthToken: getAuthToken
  }
);

/**
 * A2A Message Service
 */
export const a2aMessageService = {
  /**
   * Get message groups
   */
  async getMessageGroups(params) {
    const { tenantId, userId, page = 1, limit = 50 } = params;
    
    const result = await a2aClient.executeTask('list_message_groups', {
      tenant_id: tenantId,
      user_id: userId,
      page,
      limit
    });

    return {
      success: result.success,
      data: result.data || [],
      pagination: result.pagination
    };
  },

  /**
   * Get message group by ID
   */
  async getMessageGroupById(groupId, tenantId) {
    const result = await a2aClient.executeTask('get_group', {
      tenant_id: tenantId,
      group_id: groupId
    });

    return {
      success: result.success,
      data: result.group
    };
  },

  /**
   * Create message group
   */
  async createMessageGroup(groupData) {
    const result = await a2aClient.executeTask('create_group', {
      tenant_id: groupData.tenant_id,
      group_data: groupData
    });

    return {
      success: result.success,
      data: result.group
    };
  },

  /**
   * Send message
   */
  async sendMessage(groupId, senderId, tenantId, message, attachments = []) {
    const result = await a2aClient.executeTask('send_message', {
      tenant_id: tenantId,
      group_id: groupId,
      sender_id: senderId,
      message,
      attachments
    });

    return {
      success: result.success,
      data: result.message
    };
  },

  /**
   * Get message history
   */
  async getMessageHistory(groupId, tenantId, params = {}) {
    const { page = 1, limit = 20 } = params;
    
    const result = await a2aClient.executeTask('get_message_history', {
      tenant_id: tenantId,
      group_id: groupId,
      page,
      limit
    });

    return {
      success: result.success,
      data: result.messages || [],
      pagination: result.pagination
    };
  },

  /**
   * Mark messages as read
   */
  async markMessagesRead(messageIds, userId, tenantId) {
    const result = await a2aClient.executeTask('mark_messages_read', {
      tenant_id: tenantId,
      message_ids: messageIds,
      user_id: userId
    });

    return {
      success: result.success
    };
  },

  /**
   * Delete message
   */
  async deleteMessage(messageId, tenantId) {
    const result = await a2aClient.executeTask('delete_message', {
      tenant_id: tenantId,
      message_id: messageId
    });

    return {
      success: result.success
    };
  },

  /**
   * Add group members
   */
  async addGroupMembers(groupId, memberIds, tenantId) {
    const result = await a2aClient.executeTask('add_group_members', {
      tenant_id: tenantId,
      group_id: groupId,
      member_ids: memberIds
    });

    return {
      success: result.success
    };
  }
};

export default a2aMessageService;

